@if (editingHere()) {
  <div
    data-testid="plan-edit-bar"
    role="region"
    [attr.aria-label]="'features.plan.edit.bar.editing' | translate"
    class="border-default bg-surface flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1 rounded-sm border py-1 pr-1 pl-3 text-xs transition-shadow"
    [class]="
      mode() === 'disconnected'
        ? 'shadow-[inset_3px_0_0_var(--color-warning)]'
        : 'shadow-[inset_3px_0_0_var(--color-accent-main)]'
    ">
    <span class="inline-flex min-w-0 items-center gap-1.5">
      <i
        [class]="ICONS.pencilSimple + ' text-accent-main shrink-0 text-sm'"
        aria-hidden="true"></i>
      <span class="text-text-primary font-semibold">
        {{ 'features.plan.edit.bar.editing' | translate }}
      </span>
      <span class="text-text-secondary">
        {{ 'features.plan.edit.bar.saved_only_on_save' | translate }}
      </span>
    </span>

    <span class="bg-default h-4 w-px shrink-0" aria-hidden="true"></span>

    <span class="inline-flex shrink-0 items-center">
      <button
        #undoBtn
        type="button"
        data-testid="plan-edit-bar-undo"
        class="btn btn-icon"
        [attr.aria-label]="'features.plan.edit.bar.undo' | translate: { keys: undoKeys }"
        [disabled]="!canUndo()"
        (click)="undo()">
        <i
          [class]="ICONS.arrowCounterClockwise + ' text-sm leading-none'"
          aria-hidden="true"></i>
      </button>
      <app-tooltip
        [target]="undoBtn"
        [content]="'features.plan.edit.bar.undo' | translate: { keys: undoKeys }"
        [disabled]="!canUndo()" />
      <button
        #redoBtn
        type="button"
        data-testid="plan-edit-bar-redo"
        class="btn btn-icon"
        [attr.aria-label]="'features.plan.edit.bar.redo' | translate: { keys: redoKeys }"
        [disabled]="!canRedo()"
        (click)="redo()">
        <i [class]="ICONS.arrowClockwise + ' text-sm leading-none'" aria-hidden="true"></i>
      </button>
      <app-tooltip
        [target]="redoBtn"
        [content]="'features.plan.edit.bar.redo' | translate: { keys: redoKeys }"
        [disabled]="!canRedo()" />
    </span>

    <span class="bg-default h-4 w-px shrink-0" aria-hidden="true"></span>

    <span
      data-testid="plan-edit-bar-status"
      role="status"
      [attr.data-timings]="session().timings"
      class="inline-flex min-w-0 items-center gap-1.5"
      [class]="mode() === 'disconnected' ? 'text-text-primary' : 'text-text-secondary'">
      @switch (mode()) {
        @case ('disconnected') {
          <i
            [class]="ICONS.wifiSlash + ' text-warning shrink-0 text-sm'"
            aria-hidden="true"></i>
          {{ 'features.plan.edit.bar.disconnected' | translate }}
        }
        @case ('opening') {
          <i
            [class]="
              ICONS.circleNotch + ' shrink-0 animate-spin motion-reduce:animate-none'
            "
            aria-hidden="true"></i>
          {{ 'features.plan.edit.bar.opening' | translate }}
        }
        @default {
          @switch (session().timings) {
            @case ('recomputing') {
              <i
                [class]="
                  ICONS.circleNotch + ' shrink-0 animate-spin motion-reduce:animate-none'
                "
                aria-hidden="true"></i>
            }
            @case ('provisional') {
              <i [class]="ICONS.warning + ' text-warning shrink-0'" aria-hidden="true"></i>
            }
            @default {
              <i [class]="ICONS.check + ' shrink-0'" aria-hidden="true"></i>
            }
          }
          {{ 'features.plan.edit.bar.timings.' + session().timings | translate }}
        }
      }
    </span>

    <span class="flex-1"></span>

    <span class="inline-flex shrink-0 flex-wrap items-center gap-1.5">
      <button
        type="button"
        data-testid="plan-edit-bar-findings"
        class="inline-flex h-5 cursor-pointer items-center gap-1 rounded-full px-2 font-semibold transition-colors"
        [class]="
          findingCount() === 0
            ? 'bg-interactive text-text-secondary hover:bg-interactive/70'
            : blockingCount() > 0
              ? 'bg-danger-soft text-danger hover:bg-danger-soft/70'
              : 'bg-warning-soft text-warning-hover hover:bg-warning-soft/70'
        "
        (click)="planPanel.reveal('findings')">
        @if (findingsUnknown()) {
          <span>{{ 'features.plan.edit.bar.findings.unknown' | translate }}</span>
        } @else {
          <i
            [class]="
              (findingCount() === 0
                ? ICONS.check
                : blockingCount() > 0
                  ? ICONS.prohibit
                  : ICONS.warning) + ' text-xs'
            "
            aria-hidden="true"></i>
          <span class="tabular-nums">
            {{
              'features.plan.edit.bar.findings.' +
                (findingCount() === 0 ? 'none' : findingCount() === 1 ? 'one' : 'other')
                | translate: { count: findingCount() }
            }}
          </span>
        }
        @if (blockingCount() > 0) {
          <span class="font-medium tabular-nums opacity-80">
            {{
              'features.plan.edit.bar.findings_blocking'
                | translate: { count: blockingCount() }
            }}
          </span>
        }
      </button>

      @if (unsavedCount() > 0) {
        <span
          data-testid="plan-edit-bar-unsaved"
          class="bg-interactive text-text-secondary inline-flex h-5 items-center rounded-full px-2 font-semibold tabular-nums">
          {{
            'features.plan.edit.bar.unsaved.' + (unsavedCount() === 1 ? 'one' : 'other')
              | translate: { count: unsavedCount() }
          }}
        </span>
        <button
          type="button"
          data-testid="plan-edit-bar-discard"
          class="btn btn-cancel"
          [disabled]="!canClose()"
          (click)="discard()">
          {{ 'features.plan.edit.bar.discard' | translate }}
        </button>
        <button
          type="button"
          data-testid="plan-edit-bar-save"
          class="btn btn-primary"
          [disabled]="mode() !== 'editing'"
          (click)="save()">
          @if (mode() === 'saving') {
            <i
              [class]="
                ICONS.circleNotch +
                ' animate-spin text-sm leading-none motion-reduce:animate-none'
              "
              aria-hidden="true"></i>
            {{ 'features.plan.edit.bar.saving' | translate }}
          } @else {
            {{ 'features.plan.edit.bar.save' | translate }}
          }
        </button>
      } @else {
        <button
          type="button"
          data-testid="plan-edit-bar-done"
          class="btn btn-primary"
          [disabled]="!canClose()"
          (click)="done()">
          {{ 'features.plan.edit.bar.done' | translate }}
        </button>
      }
    </span>
  </div>
}
